import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SECTIONS, QUESTIONS } from '../data/questions.js';
import Field from '../components/Field.jsx';
import { useDocumentMeta } from '../lib/use-document-meta.js';
import { metaForPath } from '../lib/route-meta.js';
import './Intake.css';

const STORAGE_KEY = 'margin-intake-draft-v1';

export default function Intake() {
  useDocumentMeta(metaForPath('/intake'));

  const [sectionIndex, setSectionIndex] = useState(0);
  const [responses, setResponses] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(responses));
  }, [responses]);

  const section = SECTIONS[sectionIndex];
  const sectionQuestions = useMemo(
    () => QUESTIONS.filter((q) => q.section === section.id).sort((a, b) => a.order - b.order),
    [section.id]
  );

  const setValue = (id, value) => {
    setResponses((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const validateSection = () => {
    const next = {};
    sectionQuestions.forEach((q) => {
      if (!q.required) return;
      const v = responses[q.id];
      if (q.type === 'multiselect') {
        if (!Array.isArray(v) || v.length === 0) next[q.id] = 'Pick at least one.';
      } else if (q.type === 'number') {
        if (v === '' || v === undefined || v === null) next[q.id] = 'Required.';
      } else if (q.type === 'calendar') {
        if (typeof v !== 'string' || v.trim() === '') next[q.id] = 'Please pick a time.';
      } else {
        if (typeof v !== 'string' || v.trim() === '') next[q.id] = 'Required.';
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goNext = () => {
    if (!validateSection()) return;
    if (sectionIndex < SECTIONS.length - 1) {
      setSectionIndex(sectionIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleSubmit();
    }
  };

  const goBack = () => {
    if (sectionIndex > 0) {
      setSectionIndex(sectionIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/submit-intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responses }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Submission failed (${res.status})`);
      }
      setSubmitted(true);
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error('[intake] submit failed', err);
      setSubmitError(err.message || 'Something went wrong. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="intake-shell">
        <header className="intake-header">
          <Link to="/" className="intake-wordmark">Margin</Link>
        </header>
        <main className="intake-done">
          <h1>What you just wrote isn't lost. It's the start of the work.</h1>
          <p>You'll get a confirmation shortly with everything you shared. I'll start there.</p>
          <Link to="/" className="intake-link">Back to Margin →</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="intake-shell">
      <header className="intake-header">
        <Link to="/" className="intake-wordmark">Margin</Link>
        <nav className="intake-nav" aria-label="Sections">
          {SECTIONS.map((s, i) => (
            <span
              key={s.id}
              className={`intake-nav-item ${i === sectionIndex ? 'on' : ''} ${i < sectionIndex ? 'done' : ''}`}
              aria-current={i === sectionIndex ? 'step' : undefined}
            >
              {s.title}
            </span>
          ))}
        </nav>
      </header>

      <main className="intake-main">
        <div className="intake-section-label">
          Section {sectionIndex + 1} of {SECTIONS.length}
        </div>
        <h1 className="intake-section-title">{section.title}</h1>
        {section.intro && <p className="intake-section-intro">{section.intro}</p>}

        <form className="intake-form" onSubmit={(e) => { e.preventDefault(); goNext(); }}>
          {sectionQuestions.map((q) => (
            <Field
              key={q.id}
              question={q}
              value={responses[q.id]}
              onChange={(v) => setValue(q.id, v)}
              error={errors[q.id]}
            />
          ))}

          {submitError && (
            <div className="intake-submit-error" role="alert">
              {submitError}
            </div>
          )}

          <div className="intake-actions">
            {sectionIndex > 0 && (
              <button type="button" className="intake-btn-secondary" onClick={goBack} disabled={submitting}>
                Back
              </button>
            )}
            <button type="submit" className="intake-btn-primary" disabled={submitting}>
              {submitting
                ? 'Sending…'
                : sectionIndex === SECTIONS.length - 1
                  ? 'Send it'
                  : 'Continue'}
            </button>
          </div>
        </form>
      </main>

      <footer className="intake-footer">
        <span>Margin</span>
        <span>margin.consulting</span>
      </footer>
    </div>
  );
}
